import { useCallback, useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Box, Button, Stack } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import GridOnIcon from '@mui/icons-material/GridOn';
import Crop169Icon from '@mui/icons-material/Crop169';
import DownloadIcon from '@mui/icons-material/Download';
import {
  abyssRotationFromViewerRotation,
  Number3,
  adjustPoseForRotationCenter,
  undoAdjustPoseForRotationCenter,
  composeThreeMatrix4,
  decomposeThreeMatrix4,
  convertEnuScansTransfromToNedCadTransform,
} from '@fabric-web/3d-viewer';
import { EdlStrengthSlider } from '@/components/shared/EdlStrengthSlider';
import * as state from '../state';
import { ToggleItemVisibility } from '../../Viewer/Panels/Visibility/ToggleItemVisibility';
import { PointSizeControls } from '@/components/shared/PointSizeControls';
import { VisibilityBoxPanel } from './VisibilityBoxPanel';
import { PointBudgetControls } from '@/components/shared/PointBudgetControls';
import { PoseFields } from './PoseFields';
import { useScansTransform } from '../hooks/useScansTransform';
import { useUpdateScansToCadTransformMutation } from '@/__generated__/graphql';
import { AxesSelector } from './AxesSelector';
import { triggerFileDownload } from '../util';
import { ActionMenu } from './ActionMenu';
import { ALL_ONES, ALL_ZEROS } from '../constants';

type Props = {
  structureId: string;
};

export const LeftToolbar = ({ structureId }: Props) => {
  const [isScansVisible, setIsScansVisible] = useAtom(state.isScansVisible);
  const [isCadVisible, setIsCadVisible] = useAtom(state.isCadVisible);

  const sceneBoundingBox = useAtomValue(state.sceneBoundingBox);

  const transformMode = useAtomValue(state.transformMode);
  const rotationCenter = useAtomValue(state.rotationCenter);

  const {
    scansTransform,
    setScansTransform,
    undoScansTransform,
    redoScansTransform,
    scansTransformWithHistory,
  } = useScansTransform();

  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);

  const currentPose = useMemo(() => {
    if (!scansTransform) return undefined;
    return adjustPoseForRotationCenter(scansTransform, rotationCenter);
  }, [scansTransform, rotationCenter]);

  const [updateScansToCadTransform] = useUpdateScansToCadTransformMutation({
    // refetchQueries: [GetStructureForCadAlignmentDocument],
  });

  const setPoseValues = useCallback(
    (component: 'position' | 'rotation', values: Number3) => {
      const newPose = {
        ...(currentPose ?? { position: ALL_ZEROS, rotation: ALL_ZEROS, scale: ALL_ONES }),
      };
      newPose[component] = values;
      const newScansTransform = undoAdjustPoseForRotationCenter(newPose, rotationCenter);
      setScansTransform(newScansTransform);
    },
    [currentPose, rotationCenter, setScansTransform]
  );

  const saveScansToCadTransform = useCallback(async () => {
    if (scansTransform) {
      const scansToCadTransform = [
        ...scansTransform.position,
        ...abyssRotationFromViewerRotation(scansTransform.rotation),
      ];
      await updateScansToCadTransform({ variables: { structureId, scansToCadTransform } });
    }
  }, [structureId, scansTransform, updateScansToCadTransform]);

  const getTransformFrameString = useCallback(
    (isInverse: boolean): string | undefined => {
      if (!scansTransform) return undefined;

      let transform = scansTransform;
      if (isInverse) {
        const mat4 = composeThreeMatrix4(transform);
        convertEnuScansTransfromToNedCadTransform(mat4);
        transform = decomposeThreeMatrix4(mat4);
      }

      const abyssRotation = abyssRotationFromViewerRotation(scansTransform.rotation);

      const transformFrameString = [...scansTransform.position, ...abyssRotation]
        .map((value) => value.toString())
        .join(',');
      return transformFrameString;
    },
    [scansTransform]
  );

  const getTransformMatrixString = useCallback(
    (isInverse: boolean): string | undefined => {
      if (!scansTransform) return undefined;

      const mat4 = composeThreeMatrix4(scansTransform);
      if (isInverse) {
        convertEnuScansTransfromToNedCadTransform(mat4);
      }
      const matrixString = mat4
        .toArray()
        .map((value) => value.toString())
        .join(',');
      return matrixString;
    },
    [scansTransform]
  );

  const copyTransformFrame = useCallback(
    (isInverse: boolean) => {
      const frame = getTransformFrameString(isInverse);
      if (!frame) return;

      const info = isInverse ? 'CAD to Scans (NED)' : 'Scans to CAD (ENU)';
      console.log(`${info} transform Frame`, frame);
      navigator.clipboard.writeText(frame);
      setIsDownloadMenuOpen(false);
    },
    [getTransformFrameString]
  );

  const copyTransformMatrix = useCallback(
    (isInverse: boolean) => {
      const matrixString = getTransformMatrixString(isInverse);
      if (!matrixString) return;

      const info = isInverse ? 'CAD to Scans (NED)' : 'Scans to CAD (ENU)';
      console.log(`${info} transform Matrix`, matrixString);
      navigator.clipboard.writeText(matrixString);
      setIsDownloadMenuOpen(false);
    },
    [getTransformMatrixString]
  );

  const downloadTransformJson = useCallback(
    (isInverse: boolean) => {
      const frame = getTransformFrameString(isInverse);
      if (!frame) return;

      // https://abyss-solutions.atlassian.net/wiki/spaces/PD/pages/3497590787/SOP+CAD+Alignment
      // prep-model.sh
      // transform-params.json

      // CAD to Scans (in NED, existing workflow):
      // T is NED-ENU rotation
      // cad_points_ned = T * cad_points_enu
      // transfrom.frame is rotation,translation of cad_points_ned to scans_points_ned
      // transfrom.rotate = "y,x,-z"

      // Scans to CAD (in ENU)
      // scans_points_enu (octree)
      // cad_points_enu (cad_octree)
      // transfrom.frame = scansTransform (is rotation,translation of enu_scans_points to enu_cad_points)
      // transfrom.rotate = "x,y,z"

      const data = {
        frame,
        rotate: isInverse ? 'y,x,-z' : 'x,y,z',
        transformType: isInverse ? 'cad-to-scans' : 'scans-to-cad',
        eulerOrder: 'xyz', // abyss convention ('XYZ' - Three js convention)
      };
      console.log('transform-params', data);
      triggerFileDownload(data, 'transform-params.json', 'application/json');
      setIsDownloadMenuOpen(false);
    },
    [getTransformFrameString]
  );

  const [downloadButtonElem, setDownloadButtonElem] = useState<null | HTMLElement>(null);

  const copyTransformMenuComp = useMemo(() => {
    if (!isDownloadMenuOpen) return undefined;
    return (
      <ActionMenu
        id="download-menu"
        aria-labelledby="download-button"
        anchorEl={downloadButtonElem}
        open={isDownloadMenuOpen}
        onClose={() => setIsDownloadMenuOpen(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        items={[
          {
            name: 'Download Scans to CAD Transform Json (ENU)',
            onClick: () => downloadTransformJson(false),
            icon: <DownloadIcon />,
          },
          {
            name: 'Copy Scans to CAD Transform Frame (ENU)',
            onClick: () => copyTransformFrame(false),
            icon: <Crop169Icon />,
          },
          {
            name: 'Copy Scans to CAD Transform Matrix (ENU)',
            onClick: () => copyTransformMatrix(false),
            icon: <GridOnIcon />,
          },
          {}, // divider
          {
            name: 'Download CAD to Scans Transform Json (NED)',
            onClick: () => downloadTransformJson(true),
            icon: <DownloadIcon />,
          },
          {
            name: 'Copy CAD to Scans Transform Frame (NED)',
            onClick: () => copyTransformFrame(true),
            icon: <Crop169Icon />,
          },
          {
            name: 'Copy CAD to Scans Transform Matrix (NED)',
            onClick: () => copyTransformMatrix(true),
            icon: <GridOnIcon />,
          },
        ]}
      />
    );
  }, [
    isDownloadMenuOpen,
    downloadTransformJson,
    copyTransformFrame,
    copyTransformMatrix,
    downloadButtonElem,
  ]);

  const { history, undoIndex } = scansTransformWithHistory ?? {};
  const undoCount = undoIndex ?? 0;
  const redoCount = Math.max(0, (history?.length ?? 0) - (undoIndex ?? 0) - 1);

  return (
    <>
      <Stack sx={{ overflowY: 'auto' }}>
        <Box sx={{ paddingY: 1 }}>
          <VisibilityBoxPanel defaultBoundingBox={sceneBoundingBox} />
        </Box>
        <Box sx={{ paddingY: 0.5 }}>
          <ToggleItemVisibility
            label="Scans point cloud"
            isVisible={isScansVisible}
            toggleVisibility={() => setIsScansVisible((current) => !current)}
          />
        </Box>
        <Box sx={{ paddingY: 0.5 }}>
          <ToggleItemVisibility
            label="CAD point cloud"
            isVisible={isCadVisible}
            toggleVisibility={() => setIsCadVisible((current) => !current)}
          />
        </Box>

        <Stack sx={{ padding: 1 }} direction="row">
          <Box sx={{ paddingRight: 0 }}>
            <Button
              variant="contained"
              onClick={undoScansTransform}
              endIcon={<UndoIcon />}
              disabled={!undoCount}
            >
              Undo {undoCount}
            </Button>
          </Box>
          <Box sx={{ paddingX: 0.5 }}>
            <Button
              variant="contained"
              onClick={redoScansTransform}
              endIcon={<RedoIcon />}
              disabled={!redoCount}
            >
              Redo {redoCount}
            </Button>
          </Box>
        </Stack>

        <PoseFields
          position={currentPose?.position ?? ALL_ZEROS}
          rotation={currentPose?.rotation ?? ALL_ZEROS}
          setPosition={(values) => setPoseValues('position', values)}
          setRotation={(values) => setPoseValues('rotation', values)}
          isDisabled={!!transformMode}
        />

        <AxesSelector />

        <Box sx={{ paddingTop: 1, paddingBottom: 2 }}>
          <Button variant="contained" onClick={saveScansToCadTransform} endIcon={<SaveIcon />}>
            Save Transform
          </Button>
        </Box>

        <Box sx={{ paddingTop: 1, paddingBottom: 2 }}>
          <Button
            ref={(elem) => setDownloadButtonElem(elem)}
            variant="contained"
            // onClick={copyTransformFrameToClipboard}
            onClick={() => setIsDownloadMenuOpen((current) => !current)}
            // endIcon={<ContentCopyIcon />}
            endIcon={<DownloadIcon />}
          >
            Download Transform
          </Button>
        </Box>
        {copyTransformMenuComp}

        <PointSizeControls />

        <EdlStrengthSlider title="Enhance Points" />

        <PointBudgetControls />
      </Stack>
    </>
  );
};
